import { LevelCatalog, LevelSession, SaveRecord, SaveService, SuspendedLevel, mayReplace, recordBestStage, resumeSession, suspendSession } from "@highmarch/app";
import { BalanceData, UpgradeJob, UpgradeTarget } from "@highmarch/domain";
import { consoleLogger, deviceMarks, systemClock, takeInstallPrompt, webDevice } from "./infra/adapters";
import { reloadWhenSaved } from "./infra/save/openSaveStore";
import type { LevelController } from "./level/levelController";
import { audio } from "./audio/audio";
import { showDialog } from "./ui/dialog";
import { ContinueInfo, setContinueInfo } from "./ui/hub/battleCard";
import { collectAwayItems, idleBuilderBadge, jobAwayItems, jobsDoneText, showAwayCard } from "./ui/hub/awayCard";
import { Readiness, readinessFor } from "./ui/hub/readiness";
import { installCardDue, showInstallCard } from "./ui/installCard";
import { ResumeProgress } from "./ui/resumeProgress";
import type { SaveBackup } from "./ui/settings";
import { toast } from "./ui/toast";
import { guide } from "./ui/guide/guide";

/** deviceMarks keys: when the page was last hidden (the away card's "Away 3 h"), and the install card's last showing / decline. */
const MARK_LAST_SEEN = "lastSeen", MARK_INSTALL_SHOWN = "installShown", MARK_INSTALL_DECLINED = "installDeclined";
/** Ticks replayed between frames on resume: ~50 ms in node, a short stall on a phone, with the bar moving between. */
const RESUME_CHUNK_TICKS = 2000;
/** A resume shorter than this shows no progress bar. */
const RESUME_PROGRESS_AFTER_MS = 250;

export interface KeeperDeps {
  readonly balance: BalanceData;
  readonly saves: SaveService;
  readonly save: SaveRecord;
  readonly catalog: LevelCatalog;
  readonly ui: HTMLElement;
  /** Writes the catalog into the save and stores it (main.ts persist). */
  readonly persist: () => void;
}

/**
 * Part of the composition root (main.ts): keeping the player's progress and bringing them back (engagement doc §A5,
 * A6, B4, B5). Suspends and resumes battles, keeps the best wave per level, greets a returning player with what
 * happened while they were away, offers the install card after a win, sets the home-screen badge and backs the save
 * up. main.ts decides when (page hidden, hub entered, level over); this decides what.
 */
export class ProgressKeeper {
  /** Clock seconds the page was last seen, until the first hub entry reads it (null afterwards). */
  private awayFrom: number | null;
  /** A level was won since the last hub entry: the install card may follow. */
  private justWon = false;
  /** Upgrades that finished during the level just left, announced once the clouds part. */
  private jobsDoneInLevel: UpgradeJob[] = [];
  /** The install card was due while Captain Bram was busy (his first session): it waits, re-tried each hub tick. */
  private installWaiting = false;

  constructor(private readonly d: KeeperDeps) {
    this.awayFrom = deviceMarks.get(MARK_LAST_SEEN) ?? d.save.updatedAt;
    // Safari may evict a tab's storage; a persistent grant (home-screen apps get one) keeps the save.
    void webDevice.persistStorage().then((ok) => consoleLogger.log("info", `storage: ${ok ? "persistent" : "best effort"}`));
  }

  get suspended(): SuspendedLevel | undefined { return this.d.save.suspended; }

  // ---------------------------------------------------------------- suspend / resume

  /**
   * Saves the live battle as a suspended one (a wave break, the page hidden). Nothing to save before the first
   * command, or once the run is over. The stored log only ever grows (mayReplace): no rewinding to before a mistake.
   */
  suspend(level: LevelController | null): void {
    if (!level || level.isFinished || level.sim.isOver || level.session.commands.length === 0) return;
    const next = suspendSession(level.session, systemClock.utcNowSeconds());
    if (!mayReplace(this.d.save.suspended, next)) return;
    this.d.save.suspended = next;
    this.d.saves.save(this.d.save);
  }

  clear(): void {
    if (!this.d.save.suspended) return;
    this.d.save.suspended = undefined;
    this.d.saves.save(this.d.save);
  }

  /** The hub's CONTINUE row for the suspended battle, if there is one. */
  continueInfo(): ContinueInfo | null {
    const s = this.d.save.suspended;
    if (!s) return null;
    let name = "";
    try { name = this.d.balance.level(s.record.level).name; } catch { /* a level this build no longer has */ }
    const mode = suspendedMode(s);
    if (mode === "endless") name = "Endless";
    if (mode === "daily") name = "Daily Road";
    if (mode === "boss" || mode === "race" || mode === "odyssey") name = MODE_NAME[mode];   // MP-21
    if (mode === "challenge") name = "Challenge";
    return { level: s.record.level, name, wave: s.stage, waves: s.stages, ...(mode ? { mode } : {}) };
  }

  /** "Abandon" (and starting another level): confirms, keeps the wave it reached as a best, drops it. Resolves whether it went. */
  async confirmAbandon(then: number | null): Promise<boolean> {
    const s = this.d.save.suspended;
    if (!s) return true;
    const mode = suspendedMode(s);
    const where = s.stage <= 0 ? "before its first wave" : mode === "endless" ? `on wave ${s.stage}` : mode === "iron" ? "in its Iron wave" : `on wave ${s.stage} of ${s.stages}`;
    const what = mode === "endless" ? "Your Endless run" : mode === "iron" ? `Level ${s.record.level}'s Iron challenge` : mode === "daily" ? "Your Daily Road battle" : mode === "challenge" ? "Your challenge battle"
      : mode === "boss" || mode === "race" || mode === "odyssey" ? `Your ${MODE_NAME[mode]} battle` : `Level ${s.record.level}`;
    const body = `${what} is paused ${where}. ${then !== null ? `Starting level ${then} abandons it.` : "Abandoning it ends that run."}${mode === "iron" ? " Today's try is spent either way." : mode === "odyssey" ? " It ends this Odyssey attempt." : ""}`;
    if (!(await showDialog(this.d.ui, { title: "ABANDON BATTLE?", body, confirm: "ABANDON", cancel: "KEEP IT", danger: true }))) return false;
    if (s.stage > 0 && suspendedMode(s) === null) recordBestStage(this.d.save, s.record.level, s.stage);
    this.clear();
    setContinueInfo(null);
    return true;
  }

  /**
   * Replays a suspended battle to where it stopped, in chunks with a frame between them, so a long run (12 000 ticks
   * is ~0.3 s in node, a few times that on a phone) never freezes the page; the progress bar shows only past a blink.
   * A log that no longer fits (a build changed the rules) is dropped with a toast, never half-resumed.
   */
  async replay(s: SuspendedLevel): Promise<LevelSession | null> {
    const t0 = performance.now();
    let progress: ResumeProgress | null = null;
    try {
      const { session, replay } = resumeSession(this.d.balance, s);
      while (!replay.advance(RESUME_CHUNK_TICKS)) {
        if (!progress && performance.now() - t0 > RESUME_PROGRESS_AFTER_MS) progress = new ResumeProgress(this.d.ui);
        progress?.set(replay.progress);
        await new Promise<void>((res) => requestAnimationFrame(() => res()));
      }
      consoleLogger.log("info", `resume: level ${s.record.level} replayed ${replay.toTick} ticks in ${Math.round(performance.now() - t0)} ms`);
      return session;
    } catch (err) {
      consoleLogger.log("warning", `resume failed: ${(err as Error).message}`);
      this.clear();
      toast(this.d.ui, "That battle slipped away: start it afresh");
      return null;
    } finally {
      progress?.dispose();
    }
  }

  /** The level's run is over having reached wave `reached`: the best is kept, the suspended copy goes. */
  runOver(level: number, reached: number, won: boolean): { previousBest: number; readiness: Readiness | null } {
    const { previous } = recordBestStage(this.d.save, level, reached);
    this.d.save.suspended = undefined;
    this.d.saves.save(this.d.save);
    // The roster the run fielded (every owned tower), as the hub and the Armoury read it: the default, the towers owned
    // at level − 1, rated a lost replay or Heroic run of an early level against fewer towers than the hub (RC1 P3-7).
    return { previousBest: previous, readiness: won ? null : readinessFor(this.d.catalog.profile, level, { roster: this.d.catalog.ownedTowers() }) };
  }

  /** Leaving a level (main.ts leaveLevel, before it persists): what finished during it, and whether it was a win. */
  leftLevel(won: boolean, jobsDone: UpgradeJob[]): void {
    this.d.save.suspended = undefined;
    if (won) this.justWon = true;
    this.jobsDoneInLevel = jobsDone;
  }

  /** Upgrades that finished during the level: a toast and the done sound once the clouds have parted. */
  announceJobs(): void {
    const done = this.jobsDoneInLevel;
    this.jobsDoneInLevel = [];
    if (done.length === 0) return;
    audio.play("hubUpgradeDone");
    toast(this.d.ui, jobsDoneText(this.d.balance, done));
  }

  // ---------------------------------------------------------------- returning

  /** The page went out of sight: remember when (time away), and badge the home-screen icon with idle builders. */
  pageHidden(): void {
    deviceMarks.set(MARK_LAST_SEEN, systemClock.utcNowSeconds());
    webDevice.setAppBadge(idleBuilderBadge(this.d.balance, this.d.catalog.profile, this.d.catalog.ownedTowers()));
  }

  /**
   * Entering the hub: the badge clears, the CONTINUE row is set, then the "while you were away" card when something
   * happened (upgrades finished while the game was closed, other features' lines) and the install card if one is due
   * after a win. `inLevel` is checked after the away card, which the player may leave open for a while.
   */
  async enterHub(done: readonly UpgradeJob[], now: number, inLevel: () => boolean): Promise<void> {
    webDevice.setAppBadge(0);
    const awaySeconds = this.awayFrom === null ? null : Math.max(0, now - this.awayFrom);
    this.awayFrom = null;                                      // once per return: the next hub entry is from a level
    const items = [...jobAwayItems(this.d.balance, done), ...collectAwayItems({ awaySeconds, now })];
    if (items.length > 0) {
      if (done.length > 0) audio.play("hubUpgradeDone");
      await showAwayCard(this.d.ui, items, awaySeconds);
    }
    if (this.justWon && !inLevel()) { this.justWon = false; this.installWaiting = true; await this.hubTick(now); }
  }

  /**
   * Every hub tick (and on entering it): a waiting install card shows once the guide is quiet (DESIGN §4.2: not over
   * the captain's first session), then stops waiting. Never dropped: it waits across hub visits until then.
   */
  async hubTick(now: number): Promise<void> {
    if (!this.installWaiting || guide()?.quiet() === false) return;
    this.installWaiting = false;
    await this.installCard(now);
  }

  /** The install card when it is due (`force`: the debug hook shows it regardless). */
  async installCard(now: number, force = false): Promise<void> {
    const due = installCardDue({ now, lastShown: deviceMarks.get(MARK_INSTALL_SHOWN), declined: deviceMarks.get(MARK_INSTALL_DECLINED) !== null, standalone: webDevice.isStandalone(), hasWon: this.d.catalog.highestLevelCleared >= 1 });
    if (!due && !force) return;
    deviceMarks.set(MARK_INSTALL_SHOWN, now);
    const choice = await showInstallCard(this.d.ui, { apple: webDevice.isAppleMobile(), prompt: takeInstallPrompt() });
    if (choice === "never") deviceMarks.set(MARK_INSTALL_DECLINED, now);
  }

  /** Debug (the browser checks): the away card with two made-up finished upgrades and three hours away. */
  debugAwayCard(): Promise<void> {
    const B = this.d.balance, t = B.towers.findIndex((x) => !x.isBase), away = 3 * 3600 + 12 * 60;
    const jobs: UpgradeJob[] = [{ id: 1, target: UpgradeTarget.Tower, towerIndex: t, toLevel: 4, startedAt: 0, endAt: 0, durationSeconds: 0 }, { id: 2, target: UpgradeTarget.Base, towerIndex: 0, toLevel: 3, startedAt: 0, endAt: 0, durationSeconds: 0 }];
    return showAwayCard(this.d.ui, [...jobAwayItems(B, jobs), ...collectAwayItems({ awaySeconds: away, now: systemClock.utcNowSeconds() })], away);
  }

  // ---------------------------------------------------------------- backup

  /** Settings → Save backup: export through the share sheet or clipboard, import through the save's own validation. */
  backup(): SaveBackup {
    return {
      exportSave: () => { this.d.persist(); return JSON.stringify(this.d.save); },
      importSave: (json) => this.importSave(json),
      share: (text) => webDevice.shareText("Highmarch progress", text),
    };
  }

  /** A pasted save, migrated and validated like a loaded one and trial-loaded, then stored; the page reloads into it. */
  private importSave(json: string): string | null {
    try {
      const record = this.d.saves.parse(json);
      LevelCatalog.fromSave(this.d.balance, record);         // a profile the game cannot load is refused here, not at boot
      this.d.saves.save(record);
    } catch (err) {
      consoleLogger.log("warning", `import refused: ${(err as Error).message}`);
      return "That's not a backup this game can read. Check you pasted all of it.";
    }
    void reloadWhenSaved();
    return null;
  }
}

/** An Iron, Endless or Daily Road run put aside (its record's modifiers say so), else null (docs/MODES.md). */
/** MP-21: the event modes' names on the CONTINUE row and the abandon dialog. */
const MODE_NAME = { boss: "Boss Week", race: "Race Week", odyssey: "Odyssey" } as const;

export function suspendedMode(s: SuspendedLevel): "iron" | "endless" | "daily" | "challenge" | "boss" | "race" | "odyssey" | null {
  const m = s.record.modifiers;
  const ev = m?.event?.mode;
  return m?.endless ? "endless" : m?.iron ? "iron" : m?.daily ? "daily" : m?.challenge ? "challenge" : ev === "boss" || ev === "race" || ev === "odyssey" ? ev : null;
}
